import React, { useState, useEffect } from 'react';
import { FirebasePatientService, Patient } from '../../../services/firebase/FirebasePatientService';
import { firebaseAppointmentService, Appointment } from '../../../services/firebase/FirebaseAppointmentService';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../Toast';

interface BillingTabProps {}

type SubTab = 'unbilled' | 'claims' | 'payments';

interface UnbilledAppointment {
  id: string;
  appointmentCount: number;
  client: Patient;
  payer: string;
  clinician: string;
  totalCharged: number;
  appointments: Appointment[];
  hasWarning: boolean;
}

interface Claim {
  id: string;
  client: Patient;
  payer: string;
  claimType: string;
  status: string;
  dateSubmitted: Date;
  amount: number;
}

interface InsurancePayment {
  id: string;
  paymentDate: Date;
  payer: string;
  paymentMethod: string;
  paymentNumber: string;
  amount: number;
  unallocated: number;
  allocatedAppointments: Appointment[];
}

const BillingTab: React.FC<BillingTabProps> = () => {
  const { user } = useAuth();
  const toast = useToast();
  const providerId = user?.uid || '';
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('unbilled');
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);

  // Filters
  const [dateRange, setDateRange] = useState({ start: '2025-09-17', end: '2025-10-16' });
  const [selectedPayer, setSelectedPayer] = useState('all');
  const [selectedNoteStatus, setSelectedNoteStatus] = useState('all');
  const [selectedClient, setSelectedClient] = useState('all');
  const [selectedClaimType, setSelectedClaimType] = useState('all');
  const [selectedClaimStatus, setSelectedClaimStatus] = useState('all');
  const [selectedAllocationStatus, setSelectedAllocationStatus] = useState('all');
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('all');
  const [sortBy, setSortBy] = useState('priority');

  // Data
  const [patients, setPatients] = useState<Patient[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [unbilledAppointments, setUnbilledAppointments] = useState<UnbilledAppointment[]>([]);
  const [claims, setClaims] = useState<Claim[]>([]);
  const [payments, setPayments] = useState<InsurancePayment[]>([]);

  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    paymentDate: new Date().toISOString().split('T')[0],
    payer: '',
    paymentMethod: '',
    paymentNumber: '',
    amount: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    if (!providerId) return;
    const allPatients = await FirebasePatientService.getAllPatients(providerId);
    setPatients(allPatients);

    const allAppointments = await firebaseAppointmentService.getAppointments({});
    setAppointments(allAppointments);

    // Group appointments by patient for unbilled view
    const grouped = groupUnbilledAppointments(allPatients, allAppointments);
    setUnbilledAppointments(grouped);

    // Mock claims data
    setClaims([]);

    // Mock payments data
    setPayments([]);
  };

  const groupUnbilledAppointments = (patients: Patient[], appointments: Appointment[]): UnbilledAppointment[] => {
    const grouped: { [key: string]: UnbilledAppointment } = {};

    // Filter completed appointments that haven't been billed
    const completedAppointments = appointments.filter(apt =>
      apt.status === 'completed' &&
      new Date(apt.appointmentDate) >= new Date(dateRange.start) &&
      new Date(apt.appointmentDate) <= new Date(dateRange.end)
    );

    completedAppointments.forEach(apt => {
      const patient = patients.find(p => p.id === apt.patientId);
      if (!patient) return;

      const key = apt.patientId;
      if (!grouped[key]) {
        grouped[key] = {
          id: key,
          appointmentCount: 0,
          client: patient,
          payer: patient.insurance?.provider || 'Self-Pay',
          clinician: '1 Clinician',
          totalCharged: 0,
          appointments: [],
          hasWarning: !patient.insurance?.provider
        };
      }

      grouped[key].appointmentCount++;
      grouped[key].totalCharged += 145; // Default session rate
      grouped[key].appointments.push(apt);
    });

    return Object.values(grouped);
  };

  const handleAddPayment = () => {
    // Add payment logic here
    console.log('Adding payment:', paymentForm);
    setShowAddPaymentModal(false);
    // Reset form
    setPaymentForm({
      paymentDate: new Date().toISOString().split('T')[0],
      payer: '',
      paymentMethod: '',
      paymentNumber: '',
      amount: 0
    });
  };

  return (
    <div style={{
      padding: '32px',
      backgroundColor: '#16325B',
      minHeight: '100vh',
      color: '#f9fafb'
    }}>
      {/* Warning Banner */}
      <div style={{
        backgroundColor: '#fef3c7',
        border: '1px solid #fbbf24',
        borderLeft: '4px solid #f59e0b',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '24px',
        color: '#92400e'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <span style={{ fontSize: '20px' }}>ℹ️</span>
          <div>
            <strong style={{ display: 'block', marginBottom: '8px' }}>Information required to submit batch claims</strong>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>
                Tax ID or Social Security number is missing in your practice settings. <button onClick={() => toast.info('Action Required', 'Navigate to Settings > Practice Info to update Tax ID.')} style={{ color: '#2563eb', textDecoration: 'underline', background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer' }}>Update now</button>
              </li>
              <li>
                Office address is missing in your practice settings. <button onClick={() => toast.info('Action Required', 'Navigate to Settings > Practice Info to update office address.')} style={{ color: '#2563eb', textDecoration: 'underline', background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer' }}>Update now</button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '24px'
      }}>
        <div>
          <h1 style={{
            fontSize: '32px',
            fontWeight: '600',
            color: '#f9fafb',
            margin: '0 0 8px 0'
          }}>
            Insurance
          </h1>
          <p style={{
            fontSize: '16px',
            color: '#d1d5db',
            margin: 0
          }}>
            Submit claims, review statuses, and record insurance payments.
          </p>
        </div>
        <button
          onClick={() => setShowAddPaymentModal(true)}
          style={{
            padding: '12px 24px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          Add insurance payment
        </button>
      </div>

      {/* Sub Tabs */}
      <div style={{
        borderBottom: '2px solid #374151',
        marginBottom: '24px',
        display: 'flex',
        gap: '32px'
      }}>
        <button
          onClick={() => setActiveSubTab('unbilled')}
          style={{
            padding: '12px 0',
            background: 'transparent',
            border: 'none',
            borderBottom: activeSubTab === 'unbilled' ? '3px solid #3b82f6' : '3px solid transparent',
            color: activeSubTab === 'unbilled' ? '#3b82f6' : '#9ca3af',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '600',
            marginBottom: '-2px'
          }}
        >
          Unbilled appointments
        </button>
        <button
          onClick={() => setActiveSubTab('claims')}
          style={{
            padding: '12px 0',
            background: 'transparent',
            border: 'none',
            borderBottom: activeSubTab === 'claims' ? '3px solid #3b82f6' : '3px solid transparent',
            color: activeSubTab === 'claims' ? '#3b82f6' : '#9ca3af',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '600',
            marginBottom: '-2px'
          }}
        >
          Claims
        </button>
        <button
          onClick={() => setActiveSubTab('payments')}
          style={{
            padding: '12px 0',
            background: 'transparent',
            border: 'none',
            borderBottom: activeSubTab === 'payments' ? '3px solid #3b82f6' : '3px solid transparent',
            color: activeSubTab === 'payments' ? '#3b82f6' : '#9ca3af',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '600',
            marginBottom: '-2px'
          }}
        >
          Payments
        </button>
      </div>

      {/* Filters Row */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '24px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Date Range */}
        <div style={{
          backgroundColor: '#e0f2fe',
          border: '1px solid #3b82f6',
          borderRadius: '6px',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#1e3a8a',
          fontSize: '14px'
        }}>
          📅 {dateRange.start} - {dateRange.end}
        </div>

        {/* Insurance Payer Filter */}
        <select
          value={selectedPayer}
          onChange={(e) => setSelectedPayer(e.target.value)}
          style={{
            padding: '8px 12px',
            backgroundColor: '#374151',
            border: '1px solid #4b5563',
            borderRadius: '6px',
            color: '#f9fafb',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          <option value="all">🛡️ All insurance payers</option>
          <option value="bcbs">Blue Cross Blue Shield</option>
          <option value="aetna">Aetna</option>
          <option value="united">UnitedHealthcare</option>
          <option value="cigna">Cigna</option>
          <option value="self-pay">Self-Pay</option>
        </select>

        {/* Conditional Filters based on sub-tab */}
        {activeSubTab === 'unbilled' && (
          <select
            value={selectedNoteStatus}
            onChange={(e) => setSelectedNoteStatus(e.target.value)}
            style={{
              padding: '8px 12px',
              backgroundColor: '#374151',
              border: '1px solid #4b5563',
              borderRadius: '6px',
              color: '#f9fafb',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <option value="all">≡ All note statuses</option>
            <option value="signed">Signed</option>
            <option value="unsigned">Unsigned</option>
            <option value="locked">Locked</option>
          </select>
        )}

        {activeSubTab === 'claims' && (
          <>
            <select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              style={{
                padding: '8px 12px',
                backgroundColor: '#374151',
                border: '1px solid #4b5563',
                borderRadius: '6px',
                color: '#f9fafb',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="all">👥 All clients</option>
              {patients.map(p => (
                <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
              ))}
            </select>
            <select
              value={selectedClaimType}
              onChange={(e) => setSelectedClaimType(e.target.value)}
              style={{
                padding: '8px 12px',
                backgroundColor: '#374151',
                border: '1px solid #4b5563',
                borderRadius: '6px',
                color: '#f9fafb',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="all">≡ All claims</option>
              <option value="original">Original</option>
              <option value="corrected">Corrected</option>
              <option value="void">Void</option>
            </select>
            <select
              value={selectedClaimStatus}
              onChange={(e) => setSelectedClaimStatus(e.target.value)}
              style={{
                padding: '8px 12px',
                backgroundColor: '#374151',
                border: '1px solid #4b5563',
                borderRadius: '6px',
                color: '#f9fafb',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="all">≡ All statuses</option>
              <option value="pending">Pending</option>
              <option value="submitted">Submitted</option>
              <option value="paid">Paid</option>
              <option value="denied">Denied</option>
            </select>
          </>
        )}

        {activeSubTab === 'payments' && (
          <>
            <select
              value={selectedAllocationStatus}
              onChange={(e) => setSelectedAllocationStatus(e.target.value)}
              style={{
                padding: '8px 12px',
                backgroundColor: '#374151',
                border: '1px solid #4b5563',
                borderRadius: '6px',
                color: '#f9fafb',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="all">≡ All allocation statuses</option>
              <option value="allocated">Fully Allocated</option>
              <option value="partially">Partially Allocated</option>
              <option value="unallocated">Unallocated</option>
            </select>
            <select
              value={selectedPaymentStatus}
              onChange={(e) => setSelectedPaymentStatus(e.target.value)}
              style={{
                padding: '8px 12px',
                backgroundColor: '#374151',
                border: '1px solid #4b5563',
                borderRadius: '6px',
                color: '#f9fafb',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="all">≡ All payment statuses</option>
              <option value="received">Received</option>
              <option value="pending">Pending</option>
            </select>
          </>
        )}

        {/* Sort By */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px', color: '#d1d5db' }}>Sort:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: '8px 12px',
              backgroundColor: '#374151',
              border: '1px solid #4b5563',
              borderRadius: '6px',
              color: '#f9fafb',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <option value="priority">By priority</option>
            <option value="date-newest">Date (newest)</option>
            <option value="date-oldest">Date (oldest)</option>
            <option value="client">Client name</option>
            <option value="amount">Amount</option>
          </select>
        </div>
      </div>

      {/* Content based on active sub-tab */}
      {activeSubTab === 'unbilled' && (
        <div style={{
          backgroundColor: '#28527A',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          {/* Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '50px 120px 1fr 1fr 120px 150px',
            gap: '16px',
            padding: '16px',
            backgroundColor: '#1e3a5f',
            borderBottom: '1px solid #374151',
            fontWeight: '600',
            fontSize: '14px',
            color: '#d1d5db'
          }}>
            <div></div>
            <div>Appointments</div>
            <div>Client</div>
            <div>Payer</div>
            <div>Clinician</div>
            <div style={{ textAlign: 'right' }}>Total charged</div>
          </div>

          {/* Table Body */}
          {unbilledAppointments.length === 0 ? (
            <div style={{
              padding: '48px',
              textAlign: 'center',
              color: '#d1d5db',
              fontSize: '16px'
            }}>
              There are no more unbilled appointments.
            </div>
          ) : (
            unbilledAppointments.map((item) => (
              <div
                key={item.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '50px 120px 1fr 1fr 120px 150px',
                  gap: '16px',
                  padding: '16px',
                  borderBottom: '1px solid #374151',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1e3a5f'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <input type="checkbox" style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                <div style={{ color: '#f9fafb', fontWeight: '500' }}>{item.appointmentCount}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: '500', cursor: 'pointer' }}>
                    {item.client.firstName} {item.client.lastName}
                  </span>
                  {item.hasWarning && (
                    <span style={{ color: '#f59e0b', fontSize: '16px' }}>⚠️</span>
                  )}
                </div>
                <div style={{ color: '#e5e7eb' }}>{item.payer}</div>
                <div style={{ color: '#e5e7eb' }}>{item.clinician} ⚠️</div>
                <div style={{ textAlign: 'right', color: '#f9fafb', fontWeight: '600' }}>
                  ${item.totalCharged}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeSubTab === 'claims' && (
        <div style={{
          padding: '48px',
          textAlign: 'center',
          backgroundColor: '#28527A',
          borderRadius: '8px'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>&#128203;</div>
          <h3 style={{ color: '#e5e7eb', marginBottom: '0.75rem', fontSize: '1.1rem' }}>Claims Submission</h3>
          <p style={{ color: '#d1d5db', fontSize: '0.95rem', marginBottom: '0.5rem' }}>
            Electronic claim filing requires a clearinghouse integration.
          </p>
          <p style={{ color: '#9ca3af', fontSize: '0.85rem', maxWidth: '500px', margin: '0 auto' }}>
            Once connected to a clearinghouse (e.g., Office Ally, Availity), you'll be able to submit claims electronically, track claim status, and manage denials from this tab.
          </p>
          <p style={{ color: '#D4A017', fontSize: '0.85rem', marginTop: '1rem' }}>
            Configure your billing settings under Settings &gt; Billing to prepare for claim submission.
          </p>
        </div>
      )}

      {activeSubTab === 'payments' && (
        <div style={{
          padding: '48px',
          textAlign: 'center',
          backgroundColor: '#28527A',
          borderRadius: '8px'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>&#128176;</div>
          <h3 style={{ color: '#e5e7eb', marginBottom: '0.75rem', fontSize: '1.1rem' }}>Insurance Payments</h3>
          <p style={{ color: '#d1d5db', fontSize: '0.95rem', marginBottom: '0.5rem' }}>
            No insurance payments recorded yet.
          </p>
          <p style={{ color: '#9ca3af', fontSize: '0.85rem', maxWidth: '500px', margin: '0 auto' }}>
            Insurance payments will appear here as claims are processed. You can also manually record payments using the "Add insurance payment" button above.
          </p>
        </div>
      )}

      {/* Add Insurance Payment Modal */}
      {showAddPaymentModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '900px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{
              margin: '0 0 24px 0',
              color: '#1e293b',
              fontSize: '28px',
              fontWeight: '600'
            }}>
              Add Insurance Payment
            </h2>

            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ fontSize: '18px', marginBottom: '16px', color: '#1e293b' }}>Payment Details</h3>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px'
              }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                    Payment Date
                  </label>
                  <input
                    type="date"
                    value={paymentForm.paymentDate}
                    onChange={(e) => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#f8fafc',
                      color: '#1e293b',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                    Payer
                  </label>
                  <select
                    value={paymentForm.payer}
                    onChange={(e) => setPaymentForm({ ...paymentForm, payer: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#f8fafc',
                      color: '#1e293b',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select Insurance Payer</option>
                    <option value="bcbs">Blue Cross Blue Shield</option>
                    <option value="aetna">Aetna</option>
                    <option value="united">UnitedHealthcare</option>
                    <option value="cigna">Cigna</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                    Payment Method
                  </label>
                  <select
                    value={paymentForm.paymentMethod}
                    onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#f8fafc',
                      color: '#1e293b',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select Method</option>
                    <option value="check">Check</option>
                    <option value="eft">EFT</option>
                    <option value="wire">Wire Transfer</option>
                    <option value="ach">ACH</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                    Payment Number
                  </label>
                  <input
                    type="text"
                    placeholder="Payment #"
                    value={paymentForm.paymentNumber}
                    onChange={(e) => setPaymentForm({ ...paymentForm, paymentNumber: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#f8fafc',
                      color: '#1e293b',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                    Amount
                  </label>
                  <input
                    type="number"
                    placeholder="$0.00"
                    value={paymentForm.amount || ''}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) || 0 })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#f8fafc',
                      color: '#1e293b',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <div style={{ fontSize: '14px', color: '#64748b' }}>
                    Unallocated: <span style={{ fontWeight: '600' }}>${paymentForm.amount || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Appointments to Allocate Section */}
            <div style={{ marginBottom: '32px' }}>
              <div style={{
                padding: '48px',
                textAlign: 'center',
                color: '#64748b',
                fontSize: '14px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px'
              }}>
                Select a payer to see appointments
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowAddPaymentModal(false)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddPayment}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Save Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingTab;
